import { createClient, SupabaseClient as SupabaseClientType } from '@supabase/supabase-js'
import { FeedLetConfig, FeedbackReport, ExitSurvey } from './types'
import { config as widgetConfig, debugLog } from './config'

export class SupabaseClient {
  private client: SupabaseClientType
  private config: FeedLetConfig

  constructor(config: FeedLetConfig) {
    this.config = config
    
    // Use provided URL/key or fallback to environment variables
    const supabaseUrl = config.supabaseUrl || widgetConfig.supabaseUrl
    const supabaseKey = config.supabaseKey || widgetConfig.supabaseAnonKey
    
    debugLog('Initializing Supabase client', { 
      url: supabaseUrl, 
      hasKey: !!supabaseKey 
    })
    
    this.client = createClient(supabaseUrl, supabaseKey)
  }

  async saveFeedbackReport(report: FeedbackReport): Promise<void> {
    const { error } = await this.client
      .from('feedback_reports')
      .insert([report])

    if (error) {
      throw new Error(`Failed to save feedback report: ${error.message}`)
    }
  }

  async saveExitSurvey(survey: ExitSurvey): Promise<void> {
    const { error } = await this.client
      .from('exit_surveys')
      .insert([survey])

    if (error) {
      throw new Error(`Failed to save exit survey: ${error.message}`)
    }
  }

  async uploadScreenshot(screenshot: Blob): Promise<string> {
    const fileName = `screenshot-${Date.now()}-${Math.random().toString(36).substring(2)}.png`
    const filePath = `screenshots/${fileName}`

    const { data, error } = await this.client.storage
      .from('screenshots')
      .upload(filePath, screenshot, {
        contentType: 'image/png',
        cacheControl: '3600'
      })

    if (error) {
      throw new Error(`Failed to upload screenshot: ${error.message}`)
    }

    // Get public URL
    const { data: publicData } = this.client.storage
      .from('screenshots')
      .getPublicUrl(filePath)

    return publicData.publicUrl
  }

  async uploadScreenshots(screenshots: Blob[]): Promise<string[]> {
    if (!screenshots || screenshots.length === 0) {
      return []
    }

    const uploadPromises = screenshots.map((screenshot, index) => {
      const fileName = `screenshot-${Date.now()}-${index}-${Math.random().toString(36).substring(2)}.png`
      const filePath = `screenshots/${fileName}`

      return this.client.storage
        .from('screenshots')
        .upload(filePath, screenshot, {
          contentType: 'image/png',
          cacheControl: '3600'
        })
        .then(({ data, error }) => {
          if (error) {
            throw new Error(`Failed to upload screenshot ${index + 1}: ${error.message}`)
          }
          
          // Get public URL
          const { data: publicData } = this.client.storage
            .from('screenshots')
            .getPublicUrl(filePath)
          
          return publicData.publicUrl
        })
    })

    try {
      return await Promise.all(uploadPromises)
    } catch (error) {
      throw new Error(`Failed to upload screenshots: ${error}`)
    }
  }

  async testConnection(): Promise<boolean> {
    try {
      const { data, error } = await this.client
        .from('feedback_reports')
        .select('count', { count: 'exact' })
        .limit(1)

      return !error
    } catch (error) {
      console.error('Supabase connection test failed:', error)
      return false
    }
  }

  async getCommentById(commentId: string): Promise<any> {
    try {
      const { data, error } = await this.client
        .from('feedback_reports')
        .select('*')
        .eq('id', commentId)
        .eq('is_contextual', true)
        .single()

      if (error) {
        throw new Error(`Failed to fetch comment: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Failed to load comment:', error)
      throw error
    }
  }
} 